import { SceneObjectRenderer } from '@components/ViewerManager/SceneObjectRenderer'
import { GIZMO_SCALE_3D, GIZMO_VISUAL_LINE_WIDTH_3D, ITEM_SCENE_SCALE } from '@constants'
import { useSharedCubeCamera } from '@context/CubeCameraContext'
import { useGLBInstance } from '@hooks/useGLBInstance'
import { useItemScale } from '@hooks/useItemScale'
import { PivotControls } from '@react-three/drei'
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { AnyID, Item } from '@types'
import { calculateDeltasBetweenSelectedItems } from '@utils/calcDeltaItems'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import {
  dragColliderRaycastPreferPivotGizmo,
  enablePivotGizmoLineRaycasts,
  eventTargetsPivotGizmo,
  getPivotGizmoSiblingGroup,
  ITEM_DRAG_COLLIDER_NAME,
  meshRaycastDeferActiveUnitRotator,
} from '@utils/sceneObjects/items/meshRaycastPreferPivotGizmo'
import {
  getActiveRotatorGizmoRoot,
  setActiveRotatorGizmoRoot,
} from '@utils/sceneObjects/items/pivotGizmoRegistry'
import { pauseHistoryRecording, resumeHistoryRecording } from '@utils/historyRecording'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box3, Matrix3, Matrix4, Object3D, Plane, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { SelectionOutline } from './SelectionOutline'

type Props = {
  id: Item['id']
  catalogID: Item['catalogID']
  matrix: Item['matrix']
  glbSize: Item['glbSize']
}

export const Item3D = React.memo(function Item3D({ id, catalogID, matrix, glbSize }: Props) {
  const [hovered, setHovered] = useState(false),
    select = useAppStore.use.select(),
    item = useAppStore((s) => s.items.get(id)) as Item,
    isSelected = useAppStore((store) => {
      const item = store.items.get(id)
      return store.layers[store.currentLayer].selected.has(id) && item?.visible
    }),
    removeSnappedWall = useAppStore.use.removeSnappedWall(),
    sceneBoundingBoxes = useAppStore.use.sceneBoundingBoxes(),
    prefs = useAppStore.use.prefs(),
    setCurrentObjectID = useAppStore.use.setCurrentObjectID(),
    mode = useAppStore((s) => s.mode),
    isBlocked = useAppStore((s) => (s.items.get(id) as Item & { isBlocked?: boolean })?.isBlocked),
    isModalOpen = useAppStore.use.isModalOpen(),
    dimensionsLinesMovable = useAppStore.use.dimensionsLinesMovable() ?? true,
    projectItemOnWall = useAppStore.use.projectItemOnWall(),
    projectItemOnPartition = useAppStore.use.projectItemOnPartition(),
    itemRef = useRef(null),
    visible = item?.visible,
    controls = useThree((state) => state.controls),
    setMode = useAppStore.use.setMode(),
    zones = item?.zones,
    morphs = item?.morphs,
    textures = item?.textures,
    currentObjectID = useAppStore.use.currentObjectID(),
    contadorModificaciones = useAppStore((s) => s.contadorModificaciones),
    controlsRef = useRef(undefined) as { current: string | undefined },
    globalPointerDownRef = useRef(false),
    originalModeRef = useRef<string | undefined>(undefined),
    editItem = useAppStore.use.editItem(),
    // editItemHeight = useAppStore.use.editItemHeight(),
    glb = useGLBInstance(String(catalogID)),
    selectedItems = useAppStore((store) => store.layers[store.currentLayer].selected) as Set<AnyID>,
    setDeltas = useAppStore.use.setDeltas(),
    captureMultiSelectRotationSnapshot = useAppStore.use.captureMultiSelectRotationSnapshot(),
    clearMultiSelectRotationSnapshot = useAppStore.use.clearMultiSelectRotationSnapshot(),
    snappedWall = useRef(''),
    { sizeRef, scaleVector } = useItemScale(id, morphs, glbSize),
    unselectAll = useAppStore.use.unselectAll(),
    updateSelectedObjectsTraslation = useAppStore.use.updateSelectedObjectsTraslation(),
    updateSelectedObjectsPosition = useAppStore.use.updateSelectedObjectsPosition(),
    gizmoColorX = isSelected && currentObjectID === id ? prefs['C/GIZMOX'] : prefs['C/GIZMOZ'],
    gizmoColorZ = isSelected && currentObjectID === id ? prefs['C/GIZMOY'] : prefs['C/GIZMOZ'],
    getInitialAngleDegrees = useAppStore.use.getInitialAngleDegrees()

  useEffect(() => {
    useAppStore.subscribe((s) => (snappedWall.current = s.items.get(id ?? '')?.snappedWall || ''))
  }, [id])

  // Limpiar estado de arrastre cuando se selecciona otro item
  useEffect(() => {
    if (
      currentObjectID &&
      currentObjectID !== id &&
      (isDraggingFromMesh || isDraggingRef.current)
    ) {
      cleanupDragState()
    }
  }, [currentObjectID, id])

  // Función para limpiar el estado de arrastre - MEJORADA
  function cleanupDragState() {
    isPointerDownRef.current = false
    globalPointerDownRef.current = false
    isDraggingRef.current = false
    setIsDraggingFromMesh(false)
    setDragPlane(null)
    setInitialDragPosition(null)
    setInitialDragMatrix(null)
    dragStartPositionRef.current = null
    hasMovedRef.current = false
    controlsRef.current = undefined
    setHovered(false)
    document.body.style.cursor = ''

    if (mode === 'MODE_DRAGGING_ITEM_3D') {
      if (originalModeRef.current) {
        setMode(originalModeRef.current)
        originalModeRef.current = undefined
      } else {
        setMode('MODE_3D_VIEW')
      }
    }
  }

  // Estados para arrastre desde cualquier parte del GLB - DEBEN estar antes de cualquier useEffect que los use
  const [isDraggingFromMesh, setIsDraggingFromMesh] = useState(false)

  const [dragPlane, setDragPlane] = useState<Plane | null>(null)
  const [initialDragPosition, setInitialDragPosition] = useState<Vector3 | null>(null)
  const [initialDragMatrix, setInitialDragMatrix] = useState<Matrix4 | null>(null)
  const dragStartPositionRef = useRef<Vector3 | null>(null)
  const hasMovedRef = useRef(false)
  const isDraggingRef = useRef(false) // Ref para rastrear si estamos arrastrando (para el listener global)
  const isPointerDownRef = useRef(false) // Ref para rastrear si el botón está presionado
  const { camera, raycaster, pointer } = useThree()

  const pivotInteractable = isSelected && currentObjectID === id && !isBlocked && !isModalOpen

  // GLB sin raycast (no tapa el arco); collider de arrastre + arcos con Line2 activos.
  useEffect(() => {
    const contentRoot = itemRef.current as Object3D | null
    const sizeRoot = sizeRef.current as Object3D | null
    if (!contentRoot || !sizeRoot) return

    const gizmoRoot = pivotInteractable ? getPivotGizmoSiblingGroup(contentRoot) : null
    const restored = new Map<Object3D, Object3D['raycast']>()

    if (pivotInteractable) {
      if (gizmoRoot) {
        enablePivotGizmoLineRaycasts(gizmoRoot)
        setActiveRotatorGizmoRoot(gizmoRoot)
      }

      sizeRoot.traverse((obj) => {
        const mesh = obj as Object3D & { isMesh?: boolean; name?: string }
        if (!mesh.isMesh) return
        const original = mesh.raycast.bind(mesh)

        if (mesh.name === ITEM_DRAG_COLLIDER_NAME && gizmoRoot) {
          restored.set(obj, original)
          mesh.raycast = function raycast(raycaster, intersects) {
            dragColliderRaycastPreferPivotGizmo.call(
              this,
              raycaster,
              intersects,
              gizmoRoot,
              original,
            )
          }
          return
        }

        restored.set(obj, original)
        mesh.raycast = () => null
      })
    }

    return () => {
      if (gizmoRoot && getActiveRotatorGizmoRoot() === gizmoRoot) {
        setActiveRotatorGizmoRoot(null)
      }
      restored.forEach((raycast, obj) => {
        obj.raycast = raycast
      })
    }
  }, [
    pivotInteractable,
    glb,
    contadorModificaciones,
    scaleVector,
    item.width,
    item.height,
    item.thickness,
  ])

  useEffect(() => {
    if (pivotInteractable) return
    const sizeRoot = sizeRef.current as Object3D | null
    if (!sizeRoot) return
    const restored = new Map<Object3D, Object3D['raycast']>()
    sizeRoot.traverse((obj) => {
      const mesh = obj as Object3D & { isMesh?: boolean }
      if (!mesh.isMesh) return
      const original = mesh.raycast.bind(mesh)
      restored.set(obj, original)
      mesh.raycast = function raycast(raycaster, intersects) {
        meshRaycastDeferActiveUnitRotator.call(this, raycaster, intersects, original)
      }
    })
    return () => {
      restored.forEach((raycast, obj) => {
        obj.raycast = raycast
      })
    }
  }, [
    pivotInteractable,
    glb,
    contadorModificaciones,
    scaleVector,
    item.width,
    item.height,
    item.thickness,
  ])

  useFrame(() => {
    if (!pivotInteractable) return
    const contentRoot = itemRef.current as Object3D | null
    if (!contentRoot) return
    const gizmoRoot = getPivotGizmoSiblingGroup(contentRoot)
    if (gizmoRoot) enablePivotGizmoLineRaycasts(gizmoRoot)
  })

  useEffect(() => {
    if (isDraggingFromMesh || isDraggingRef.current) {
      document.body.style.cursor = 'grab'
    } else if (String(currentObjectID) === String(id)) {
      document.body.style.cursor = ''
    }
    return () => {
      if (!isDraggingFromMesh && !isDraggingRef.current) {
        document.body.style.cursor = ''
      }
    }
  }, [isDraggingFromMesh, currentObjectID, id])

  useEffect(() => {
    if (String(currentObjectID) !== String(id)) return

    const endDrag = () => {
      if (
        !isPointerDownRef.current &&
        !globalPointerDownRef.current &&
        !isDraggingRef.current &&
        !isDraggingFromMesh
      ) {
        return
      }
      cleanupDragState()
    }

    const handleGlobalPointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return
      globalPointerDownRef.current = false
      endDrag()
    }

    const handleGlobalPointerCancel = () => {
      globalPointerDownRef.current = false
      endDrag()
    }

    const handleGlobalPointerMove = (e: PointerEvent) => {
      if ((e.buttons & 1) !== 0) return
      globalPointerDownRef.current = false
      endDrag()
    }

    const handleWindowBlur = () => {
      globalPointerDownRef.current = false
      endDrag()
    }

    window.addEventListener('pointerup', handleGlobalPointerUp, { capture: true })
    window.addEventListener('pointercancel', handleGlobalPointerCancel, { capture: true })
    window.addEventListener('pointermove', handleGlobalPointerMove, { capture: true })
    window.addEventListener('mouseup', handleGlobalPointerUp, { capture: true })
    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleWindowBlur)

    return () => {
      window.removeEventListener('pointerup', handleGlobalPointerUp, { capture: true } as any)
      window.removeEventListener('pointercancel', handleGlobalPointerCancel, {
        capture: true,
      } as any)
      window.removeEventListener('pointermove', handleGlobalPointerMove, { capture: true } as any)
      window.removeEventListener('mouseup', handleGlobalPointerUp, { capture: true } as any)
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleWindowBlur)
    }
  }, [mode, currentObjectID, id, isSelected])

  function handleClick(e: ThreeEvent<MouseEvent>) {
    if (isModalOpen) return

    // Limpiar estado de arrastre si se está seleccionando otro item
    if (currentObjectID && currentObjectID !== id) {
      cleanupDragState()
    }

    // Solo ejecutar click si no hubo movimiento (para distinguir entre click y drag)
    if (hasMovedRef.current) {
      hasMovedRef.current = false // Resetear para el siguiente click
      return
    }

    if (mode === 'MODE_DRAGGING_ITEM_3D' && currentObjectID && currentObjectID !== id) {
      // Limpiar el modo de arrastre cuando se selecciona otro item
      cleanupDragState()
    }

    if (
      selectedItems.size < 2 &&
      (isWall(Array.from(selectedItems)[0]) ||
        (selectedItems.size < 2 && isArea(Array.from(selectedItems)[0])))
    )
      unselectAll()

    e.stopPropagation()
    select(id, e.nativeEvent.ctrlKey || e.nativeEvent.metaKey)

    // Resetear el estado de movimiento después de seleccionar
    hasMovedRef.current = false

    if (isBlocked) return
  }

  // Arrastre desde el cuerpo del GLB (el gizmo gana si el rayo toca arco/flechas primero).
  function handleMeshPointerDown(e: ThreeEvent<PointerEvent>) {
    if (
      isModalOpen ||
      isBlocked ||
      !isSelected ||
      currentObjectID !== id ||
      !dimensionsLinesMovable
    )
      return
    if (eventTargetsPivotGizmo(e.intersections)) return

    e.stopPropagation()

    cleanupDragState()

    isPointerDownRef.current = true
    globalPointerDownRef.current = true
    dragStartPositionRef.current = e.point.clone()
    hasMovedRef.current = false
    isDraggingRef.current = false
  }

  function handleMeshPointerMove(e: ThreeEvent<PointerEvent>) {
    if ((e.buttons & 1) === 0) {
      if (
        isPointerDownRef.current ||
        globalPointerDownRef.current ||
        isDraggingFromMesh ||
        isDraggingRef.current
      ) {
        cleanupDragState()
      }
      return
    }

    // Verificar primero si el botón está presionado - si no, limpiar y salir INMEDIATAMENTE
    if (!isPointerDownRef.current) {
      // Solo limpiar si realmente había estado de arrastre activo
      if (isDraggingFromMesh || isDraggingRef.current) {
        cleanupDragState()
      }
      return
    }

    // Verificar condiciones básicas para continuar
    if (
      !dragStartPositionRef.current ||
      isModalOpen ||
      isBlocked ||
      !isSelected ||
      currentObjectID !== id ||
      !dimensionsLinesMovable
    ) {
      return
    }

    e.stopPropagation() // Siempre prevenir movimiento de cámara cuando estamos sobre el GLB seleccionado

    const moveDistance = e.point.distanceTo(dragStartPositionRef.current)

    // Iniciar arrastre con umbral más bajo y solo una vez
    if (moveDistance > 2 && !isDraggingFromMesh) {
      hasMovedRef.current = true
      isDraggingRef.current = true

      // Calcular plano horizontal en la altura del item
      const currentPosition = new Vector3()
      matrix.decompose(currentPosition, new Quaternion(), new Vector3())
      const planeNormal = new Vector3(0, 1, 0)
      const plane = new Plane(planeNormal, -currentPosition.y)

      // Calcular la intersección inicial con el plano usando el raycaster (igual que en useFrame)
      raycaster.setFromCamera(pointer, camera)
      const initialIntersection = new Vector3()
      const hasInitialIntersection = raycaster.ray.intersectPlane(plane, initialIntersection)

      setIsDraggingFromMesh(true)
      setDragPlane(plane)
      // Usar la intersección con el plano (en espacio del mundo) en lugar de e.point
      setInitialDragPosition(hasInitialIntersection ? initialIntersection.clone() : e.point.clone())
      setInitialDragMatrix(matrix.clone())
      pauseHistoryRecording()
      handleDragStart()
      setCurrentObjectID(id)
      // Cambiar cursor a "grab" cuando se inicia el arrastre
      document.body.style.cursor = 'grab'
    }
  }

  function handleMeshPointerUp(e: ThreeEvent<PointerEvent>) {
    isPointerDownRef.current = false
    globalPointerDownRef.current = false
    isDraggingRef.current = false
    if (isDraggingFromMesh || hasMovedRef.current) {
      resumeHistoryRecording()
    }
    cleanupDragState()

    if (isDraggingFromMesh || hasMovedRef.current) {
      e.stopPropagation()
    }
  }

  useFrame(() => {
    if (!isPointerDownRef.current || !globalPointerDownRef.current) {
      if (isDraggingFromMesh || isDraggingRef.current) {
        cleanupDragState()
      }
      return
    }

    // Luego verificar todos los demás valores - si falta alguno, salir (pero no limpiar si el botón está presionado)
    if (
      !isDraggingRef.current ||
      !isDraggingFromMesh ||
      !dragPlane ||
      !initialDragPosition ||
      !initialDragMatrix ||
      !isSelected ||
      currentObjectID !== id ||
      !dimensionsLinesMovable
    ) {
      return
    }

    // Asegurarse de que el modo esté establecido
    if (
      mode !== 'MODE_DRAGGING_ITEM_3D' &&
      mode !== 'MODE_FORCE_ALZADOS' &&
      mode !== 'MODE_ALZADOS'
    ) {
      if (!originalModeRef.current) {
        originalModeRef.current = mode
      }
      setMode('MODE_DRAGGING_ITEM_3D')
    }

    // Calcular intersección con el plano horizontal
    raycaster.setFromCamera(pointer, camera)
    const intersection = new Vector3()
    const hasIntersection = raycaster.ray.intersectPlane(dragPlane, intersection)

    if (hasIntersection) {
      // Calcular delta solo en X y Z (plano horizontal)
      const deltaX = intersection.x - initialDragPosition.x
      const deltaZ = intersection.z - initialDragPosition.z

      // Obtener posición inicial del item
      const initialPos = new Vector3()
      const initialRot = new Quaternion()
      const initialScale = new Vector3()
      initialDragMatrix.decompose(initialPos, initialRot, initialScale)

      // Aplicar solo el movimiento horizontal, mantener Y fijo
      const newPosition = new Vector3(
        initialPos.x + deltaX,
        initialPos.y, // Y siempre fijo
        initialPos.z + deltaZ,
      )

      const newMatrix = new Matrix4().compose(newPosition, initialRot, initialScale)
      const dl = newMatrix.clone().multiply(initialDragMatrix.clone().invert())
      const w = newMatrix.clone()
      const dw = newMatrix.clone().multiply(initialDragMatrix.clone().invert())

      handleDrag(newMatrix, dl, w, dw)
    }
  })
  function handleDragStart() {
    if (isModalOpen || isBlocked) return
    setCurrentObjectID(id)
    if (selectedItems.size > 1) {
      const currentItems = useAppStore.getState().items
      setDeltas(calculateDeltasBetweenSelectedItems(selectedItems, currentItems))
      captureMultiSelectRotationSnapshot(id)
      // Solo cambiar el modo si NO estamos en modo alzados
      // En modo alzados, los controles de cámara ya están configurados correctamente
      if (mode !== 'MODE_FORCE_ALZADOS' && mode !== 'MODE_ALZADOS') {
        // Guardar el modo original antes de cambiarlo
        originalModeRef.current = mode
        // Establecer modo de arrastre para desactivar controles de cámara
        setMode('MODE_DRAGGING_ITEM_3D')
      }
    }
  }
  function handleMissed() {
    if (isModalOpen) return
    if (mode === 'MODE_DRAWING_ITEM') {
      return setMode('MODE_3D_VIEW')
    }
  }

  const itemBB = useMemo(() => {
    return new OBB()
  }, [])

  const initialAngleDegrees = getInitialAngleDegrees(id)
  const collisionableItems = useAppStore.use.collisionableItems()
  const collideItems = useAppStore.use.collideItems()
  const removeSnappedPartition = useAppStore.use.removeSnappedPartition()

  function handleDrag<T extends Matrix4>(l: Matrix4, dl: T, w: T, dw: T) {
    if (isModalOpen || isBlocked || currentObjectID !== id || item.type === 'holes') return
    if (!itemRef.current) return
    const isRotationDrag = controlsRef.current === 'Rotator'

    if (isRotationDrag) {
      editItem(id, { matrix: w.clone() })
      const storeNow = useAppStore.getState()
      if (storeNow.setOBB) storeNow.setOBB(id)
      if (selectedItems.size > 1) {
        updateSelectedObjectsTraslation(id, w, initialAngleDegrees)
      }
    } else {
      // Guardar la posición anterior ANTES de calcular la nueva posición
      // Esto nos permite revertir si hay colisión
      const previousMatrix = item.matrix?.clone() || matrix?.clone()
      const previousPosition = new Vector3()
      if (previousMatrix) {
        previousMatrix.decompose(previousPosition, new Quaternion(), new Vector3())
      }

      const itemPosition = toVector3()
      const itemrot = toQuaternion()
      const itemScale = toVector3([1, 1, 1])
      l.decompose(itemPosition, itemrot, itemScale)
      const lr = new Matrix4().compose(itemPosition, itemrot, itemScale)

      // Actualizar el bounding box con la nueva posición ANTES de verificar colisiones
      // Usar el mismo método que setOBB para calcular el OBB correctamente
      const centerPosition = toVector3()
      const quaternion = toQuaternion()
      l.decompose(centerPosition, quaternion, toVector3())

      // Calcular el centro del item usando width, height, thickness (igual que setOBB)
      const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
      itemCenter.applyQuaternion(quaternion).add(centerPosition)

      // Crear la matriz de rotación
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

      // Actualizar el OBB directamente con las nuevas propiedades
      itemBB.center.copy(itemCenter)
      itemBB.halfSize.set(item.width / 2, item.height / 2, item.thickness / 2)
      itemBB.rotation.copy(rotationMatrix)

      let isSnapped = false
      let hasCollision = false
      let isSnappedToPartition = false
      let hasUniversalCollision = false // Bandera para colisión con items universales

      for (const [wallID, wallBB] of sceneBoundingBoxes.entries()) {
        const wallID2 = wallID.replace('COBB_', '') as AnyID
        if (itemBB.intersectsOBB(wallBB.boundingBox) && selectedItems.size === 1) {
          if (isWall(wallID2)) {
            if (snappedWall.current !== wallID2) {
              removeSnappedWall(currentObjectID)
            }
            projectItemOnWall(currentObjectID, wallID2, l, true) // true = skipContiguityCheck (arrastre manual)
            isSnapped = true
          }
          if (isPartition(wallID2)) {
            if (snappedWall.current !== wallID2) {
              removeSnappedPartition(currentObjectID)
            }
            projectItemOnPartition(currentObjectID, wallID2, l)
            isSnappedToPartition = true
          }
        }
      }

      // Verificar colisiones con otros items ANTES de aplicar el movimiento
      // IMPORTANTE: Para items universales, siempre verificar colisiones aunque no estén en collisionableItems
      const shouldCheckCollisions = collisionableItems.has(id) || item.universal === true

      if (shouldCheckCollisions) {
        // Evitar "pasar por encima" al arrastrar rápido (tunneling) con colisión continua (sweep)
        // entre la posición previa y la nueva.
        const prevPos = new Vector3()
        if (previousMatrix) {
          previousMatrix.decompose(prevPos, new Quaternion(), new Vector3())
        } else {
          prevPos.copy(itemPosition)
        }
        const nextPos = itemPosition.clone()
        const moveLen = nextPos.distanceTo(prevPos)

        const computeItemOBBAt = (pos: Vector3) => {
          const c = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
          c.applyQuaternion(quaternion).add(pos)
          const rm4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rm = new Matrix3().setFromMatrix4(rm4)
          return new OBB(c, new Vector3(item.width / 2, item.height / 2, item.thickness / 2), rm)
        }

        const sweepIntersects = (otherOBB: OBB) => {
          if (moveLen < 0.001) return computeItemOBBAt(nextPos).intersectsOBB(otherOBB)
          const minDim = Math.max(100, Math.min(item.width, item.thickness))
          const steps = Math.min(30, Math.max(4, Math.ceil(moveLen / (minDim / 4))))
          for (let i = 1; i <= steps; i++) {
            const t = i / steps
            const p = prevPos.clone().lerp(nextPos, t)
            if (computeItemOBBAt(p).intersectsOBB(otherOBB)) return true
          }
          return false
        }

        // Para items universales, verificar colisiones con TODOS los items (universales y no universales)
        // Para items no universales, verificar solo con items en collisionableItems
        for (const [otherItemId, otherItem] of items.entries()) {
          if (otherItemId === id) continue

          // Si el item actual NO es universal, solo verificar contra items collisionables
          if (item.universal !== true && !collisionableItems.has(otherItemId)) {
            continue
          }

          // Si el item actual ES universal, verificar contra todos los items de tipo 'items'
          if (item.universal === true && otherItem.type !== 'items') {
            continue
          }

          // Early exit: verificar distancia aproximada antes de calcular OBB completo
          const otherItemPos = new Vector3()
          if ('matrix' in otherItem && otherItem.matrix) {
            otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())
          } else {
            continue
          }

          const approximateDistance = itemPosition.distanceTo(otherItemPos)
          const maxPossibleDistance =
            Math.max(
              item.width + item.height + item.thickness,
              ('width' in otherItem ? otherItem.width : 0) +
                ('height' in otherItem ? otherItem.height : 0) +
                ('thickness' in otherItem ? otherItem.thickness : 0),
            ) * 1.5 // Margen de seguridad

          // Si están muy lejos, saltar la verificación detallada
          if (approximateDistance > maxPossibleDistance) {
            continue
          }

          // Intentar usar el OBB almacenado primero (más eficiente)
          const storedOBBInfo = sceneBoundingBoxes.get(otherItemId)
          let otherOBB: OBB

          if (storedOBBInfo) {
            // Usar OBB almacenado si está disponible
            otherOBB = storedOBBInfo.boundingBox
          } else {
            // Calcular OBB solo si no está almacenado y si el item tiene las propiedades necesarias
            if (!(
              'width' in otherItem &&
              'height' in otherItem &&
              'thickness' in otherItem &&
              'matrix' in otherItem
            )) {
              continue
            }

            const otherCenterPosition = toVector3()
            const otherQuaternion = toQuaternion()
            otherItem.matrix.decompose(otherCenterPosition, otherQuaternion, toVector3())

            const otherItemCenter = toVector3([
              otherItem.width / 2,
              otherItem.height / 2,
              otherItem.thickness / 2,
            ])
            otherItemCenter.applyQuaternion(otherQuaternion).add(otherCenterPosition)

            const otherRotationMatrix4 = new Matrix4().makeRotationFromQuaternion(otherQuaternion)
            const otherRotationMatrix = new Matrix3().setFromMatrix4(otherRotationMatrix4)

            otherOBB = new OBB(
              otherItemCenter,
              new Vector3(
                ('width' in otherItem ? otherItem.width : 0) / 2,
                ('height' in otherItem ? otherItem.height : 0) / 2,
                ('thickness' in otherItem ? otherItem.thickness : 0) / 2,
              ),
              otherRotationMatrix,
            )
          }

          // Verificar intersección continua (sweep) para que no pueda atravesar
          const intersects = sweepIntersects(otherOBB)

          if (intersects) {
            // Igualar el comportamiento de linkedItems: si hay colisión, NO permitir que el drag
            // avance “atravesando” el objeto. O hacemos snap (pegado) o nos quedamos en la posición previa.
            const distance2D = Math.sqrt(
              Math.pow(nextPos.x - otherItemPos.x, 2) + Math.pow(nextPos.z - otherItemPos.z, 2),
            )
            const heightDiff = Math.abs(itemPosition.y - otherItemPos.y)

            // Umbral dinámico en planta: para objetos grandes (p.ej. sofás) la distancia entre centros
            // puede ser > 750mm y aun así estar colisionando y “deberían” pegarse.
            const halfDiagXZ = Math.sqrt(
              Math.pow(item.width / 2, 2) + Math.pow(item.thickness / 2, 2),
            )
            const otherHalfDiagXZ = Math.sqrt(
              Math.pow(('width' in otherItem ? otherItem.width : 0) / 2, 2) +
                Math.pow(('thickness' in otherItem ? otherItem.thickness : 0) / 2, 2),
            )
            const MAX_SNAP_DISTANCE_2D = halfDiagXZ + otherHalfDiagXZ + 50
            const MAX_HEIGHT_DIFF = 200

            if (distance2D < MAX_SNAP_DISTANCE_2D && heightDiff < MAX_HEIGHT_DIFF) {
              // Para snap, primero fijar la transformación actual del drag (l) en el store,
              // y luego collideItems lo recoloca pegado (4 lados).
              editItem(id, {
                matrix: lr.clone(),
                x: itemPosition.x,
                y: itemPosition.y,
                z: itemPosition.z,
              })
              const storeNow = useAppStore.getState()
              if (storeNow.setOBB) storeNow.setOBB(id)
              const didSnap = collideItems(id, otherItemId)
              if (!didSnap) {
                const matrixToRevert = previousMatrix || initialDragMatrix || matrix
                if (matrixToRevert) {
                  const revertPos = new Vector3()
                  matrixToRevert.decompose(revertPos, new Quaternion(), new Vector3())
                  editItem(id, {
                    matrix: matrixToRevert.clone(),
                    x: revertPos.x,
                    y: revertPos.y,
                    z: revertPos.z,
                  })
                  const storeNow = useAppStore.getState()
                  if (storeNow.setOBB) storeNow.setOBB(id)
                }
                hasUniversalCollision = true
                return
              }
              const snappedItem = useAppStore.getState().items.get(id)
              if (snappedItem?.matrix) {
                editItem(id, {
                  x: snappedItem.x,
                  y: snappedItem.y,
                  z: snappedItem.z,
                  matrix: snappedItem.matrix.clone(),
                })
                const storeNow = useAppStore.getState()
                if (storeNow.setOBB) storeNow.setOBB(id)
              }
              hasCollision = true
              hasUniversalCollision = true // IMPORTANTE: Establecer ANTES del return
              // IMPORTANTE: ya hemos decidido el resultado de este drag tick.
              // Cortar aquí evita que más abajo se aplique una posición que atraviese/solape.
              return
            } else {
              const matrixToRevert = previousMatrix || initialDragMatrix || matrix
              if (matrixToRevert) {
                const revertPos = new Vector3()
                matrixToRevert.decompose(revertPos, new Quaternion(), new Vector3())
                editItem(id, {
                  matrix: matrixToRevert.clone(),
                  x: revertPos.x,
                  y: revertPos.y,
                  z: revertPos.z,
                })
                const storeNow = useAppStore.getState()
                if (storeNow.setOBB) storeNow.setOBB(id)
              }

              hasUniversalCollision = true
              hasCollision = true
              // IMPORTANTE: ya hemos clamped al tope en este drag tick.
              // Cortar aquí evita que más abajo se aplique una posición que atraviese/solape.
              return
            }

            break // Salir del loop cuando se detecta colisión
          }
        }
      } else if (shouldCheckCollisions) {
        // Para items no universales, mantener la lógica existente
        const itemsToCheck = Array.from(collisionableItems).filter((itemId) => itemId !== id)

        for (const collisionableItem of itemsToCheck) {
          const otherItem = items.get(collisionableItem)
          if (!otherItem) continue

          const collisionOBBInfo = sceneBoundingBoxes.get(collisionableItem)
          if (!collisionOBBInfo) continue

          const intersects = itemBB.intersectsOBB(collisionOBBInfo.boundingBox)

          if (intersects) {
            // Para items no universales (linked items), mantener la lógica de snap existente
            const otherItemPos = new Vector3()
            otherItem.matrix.decompose(otherItemPos, new Quaternion(), new Vector3())

            // Si el otro item es universal, establecer hasUniversalCollision = true
            if ((otherItem as any).universal === true) {
              hasUniversalCollision = true
            }

            const distance2D = Math.sqrt(
              Math.pow(itemPosition.x - otherItemPos.x, 2) +
                Math.pow(itemPosition.z - otherItemPos.z, 2),
            )

            const heightDiff = Math.abs(itemPosition.y - otherItemPos.y)

            const MAX_SNAP_DISTANCE_2D = 750
            const MAX_HEIGHT_DIFF = 200

            // Solo hacer snap si están cerca horizontalmente Y a una altura similar
            if (distance2D < MAX_SNAP_DISTANCE_2D && heightDiff < MAX_HEIGHT_DIFF) {
              const didSnap = collideItems(id, collisionableItem)
              if (didSnap) {
                hasCollision = true
                const snappedItem = useAppStore.getState().items.get(id)
                if (snappedItem?.matrix) {
                  editItem(id, {
                    x: snappedItem.x,
                    y: snappedItem.y,
                    z: snappedItem.z,
                    matrix: snappedItem.matrix.clone(),
                  })
                }
              }
            }
          }
        }
      }

      // Si hay colisión con item universal, revertir a la posición anterior
      // Esto previene la superposición sin hacer snap ni ajustes automáticos
      if (hasUniversalCollision) {
        // Usar previousMatrix que guardamos al inicio, o initialDragMatrix si está disponible
        const matrixToRevert = previousMatrix || initialDragMatrix || matrix
        if (matrixToRevert) {
          const revertPos = new Vector3()
          matrixToRevert.decompose(revertPos, new Quaternion(), new Vector3())

          // Revertir la posición usando editItem inmediatamente
          editItem(id, {
            matrix: matrixToRevert.clone(),
            x: revertPos.x,
            y: revertPos.y,
            z: revertPos.z,
          })

          // También actualizar el bounding box inmediatamente
          const store = useAppStore.getState()
          if (store.setOBB) {
            store.setOBB(id)
          }
        }
        // IMPORTANTE: No continuar con el movimiento ni llamar a updateSelectedObjectsPosition
        // El return previene que se ejecute el resto del código
        return
      }

      if (!hasUniversalCollision && !hasCollision && !isSnappedToPartition && !isSnapped) {
        removeSnappedWall(currentObjectID)
        removeSnappedPartition(currentObjectID)

        // Aplicar restricción de altura en modo 3D
        let restrictedY = itemPosition.y
        if (mode === 'MODE_3D_VIEW') {
          // Restringir mínimo a 0 siempre
          restrictedY = Math.max(0, restrictedY)

          // Si el item tiene altura, aplicar restricción máxima
          if (item.height) {
            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall) {
              const wall = items.get(item.snappedWall)
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            if (maxWallHeight !== null) {
              const maxYAllowed = maxWallHeight - item.height
              restrictedY = Math.min(restrictedY, maxYAllowed)
            }
          }
        }

        const restrictedMatrix = lr.clone().setPosition(itemPosition.x, restrictedY, itemPosition.z)

        editItem(currentObjectID, {
          x: itemPosition.x,
          z: itemPosition.z,
          y: restrictedY,
          matrix: restrictedMatrix,
        })
        // Pasar la matriz restringida en lugar de l para que los otros items usen el y correcto
        updateSelectedObjectsPosition(currentObjectID, restrictedMatrix)
      }
    }
  }

  const { renderTarget } = useSharedCubeCamera() || {}

  // Si el item está bloqueado, renderizar sin PivotControls
  if (isBlocked) {
    return (
      <group
        matrix={matrix}
        matrixAutoUpdate={false}
      >
        <group
          key={`${id}-${contadorModificaciones}-${textures.map((t) => `${t.zoneName}:${t.url}`).join('|')}`}
          ref={itemRef}
          name={id}
          scale={ITEM_SCENE_SCALE}
          onPointerMissed={handleMissed}
          onPointerOver={(e) => (e.stopPropagation(), setHovered(true))}
          onPointerOut={(e) => {
            setHovered(false)
            // Si el botón NO está presionado pero estábamos arrastrando, limpiar inmediatamente
            if (!isPointerDownRef.current && (isDraggingFromMesh || isDraggingRef.current)) {
              cleanupDragState()
            } else if (isDraggingFromMesh) {
              handleMeshPointerUp(e)
            }
          }}
          onPointerDown={(e) => {
            // Item bloqueado: no permitir ninguna interacción
            if (isBlocked) return
          }}
          onPointerMove={(e) => {
            // Item bloqueado: no permitir ninguna interacción
            if (isBlocked) return
          }}
          onPointerUp={(e) => {
            // Item bloqueado: no permitir ninguna interacción
            if (isBlocked) return
          }}
          onClick={(e) => {
            if (!hasMovedRef.current) {
              handleClick(e)
            }
          }}
          onDoubleClick={() => {
            isSelected &&
              controls &&
              controls?.fitToBox(itemRef.current, true, { paddingLeft: 0.5, paddingRight: 0.5 })
          }}
        >
          <group
            ref={sizeRef}
            scale={scaleVector}
            visible={visible}
          >
            {/* Outline de selección que reemplaza visualmente el gizmo - debe estar dentro del grupo con los meshes */}
            <SelectionOutline
              id={id}
              width={item.width}
              height={item.height}
              thickness={item.thickness}
              parentGroupRef={sizeRef}
              forceVisible={isSelected && !isBlocked}
              meshRevision={glb?.uuid ?? 'pending'}
            />
            <SceneObjectRenderer
              key={`${glb?.uuid}-${contadorModificaciones}`}
              object={glb}
              zones={zones}
              morphs={morphs}
              textures={textures}
              renderTarget={renderTarget}
              paginaPdf={item.paginaPdf}
              itemHeight={item.height}
              itemWidth={item.width}
              universal={item.universal}
              id={id}
            />
          </group>
        </group>
      </group>
    )
  }

  return (
    <PivotControls
      ref={itemRef}
      // rotation={[0, 2 * Math.PI, 0]}
      autoTransform={false}
      scale={GIZMO_SCALE_3D * 3} // Hacer el gizmo 3 veces más grande para cubrir todo el item
      matrix={matrix}
      visible={isSelected && !isBlocked} // Mostrar solo si está seleccionado y no está bloqueado
      onDrag={handleDrag}
      onDragStart={(e) => {
        controlsRef.current = e.component
        pauseHistoryRecording()
        handleDragStart()
      }}
      onDragEnd={() => {
        resumeHistoryRecording()
        const wasRotating = controlsRef.current === 'Rotator'
        cleanupDragState()
        clearMultiSelectRotationSnapshot()
        if (wasRotating) {
          const storeNow = useAppStore.getState()
          if (storeNow.setOBB) storeNow.setOBB(id)
          const dragItem = storeNow.items.get(id)
          if (dragItem && selectedItems.size === 1) {
            for (const [otherId, otherItem] of storeNow.items.entries()) {
              if (otherId === id || otherItem.type !== 'items') continue
              if (
                !storeNow.collisionableItems.has(otherId) &&
                (dragItem as Item).universal !== true
              ) {
                continue
              }
              const otherOBB = storeNow.sceneBoundingBoxes.get(otherId)?.boundingBox
              const dragOBB = storeNow.sceneBoundingBoxes.get(id)?.boundingBox
              if (otherOBB && dragOBB?.intersectsOBB(otherOBB)) {
                collideItems(id, otherId)
                break
              }
            }
          }
        }
      }}
      activeAxes={[
        !!(isSelected && !isBlocked), // X: movimiento horizontal permitido solo si no está bloqueado
        false, // Y: sin movimiento vertical
        !!(isSelected && !isBlocked), // Z: movimiento adelante/atrás permitido solo si no está bloqueado
      ]}
      rotationLimits={[
        [0, 0], // X-axis: bloquear inclinación
        undefined, // Y-axis: permite rotación completa (rotación vertical)
        [0, 0], // Z-axis: bloquear inclinación
      ]}
      // Mostrar el gizmo en colorAtemporal
      axisColors={['#B69533', '#B69533', '#B69533']} // colorAtemporal: rgba(182,149,51)
      lineWidth={GIZMO_VISUAL_LINE_WIDTH_3D}
      depthTest={false}
      disableRotations={false}
      disableScaling={true} // Ocultar los puntos de escala
      enabled={!isBlocked} // Desactivar completamente PivotControls cuando está bloqueado
      // opacity eliminado para mantener flechas visibles
      userData={{ pivotControlsVisible: true }}
    >
      <group
        key={`${id}-${contadorModificaciones}-${textures.map((t) => `${t.zoneName}:${t.url}`).join('|')}`}
        ref={itemRef}
        name={id}
        scale={ITEM_SCENE_SCALE}
        onPointerMissed={handleMissed}
        onClick={(e) => {
          if (!hasMovedRef.current) {
            handleClick(e)
          }
        }}
        onDoubleClick={() => {
          isSelected &&
            controls &&
            controls?.fitToBox(itemRef.current, true, { paddingLeft: 0.5, paddingRight: 0.5 })
        }}
      >
        <group
          ref={sizeRef}
          scale={scaleVector}
          visible={visible}
          onPointerDown={(e) => {
            // Solo interceptar eventos si el item está seleccionado y el movimiento está activado
            if (
              isSelected &&
              currentObjectID === id &&
              !isBlocked &&
              !isModalOpen &&
              dimensionsLinesMovable
            ) {
              handleMeshPointerDown(e)
            }
          }}
          onPointerMove={(e) => {
            // Solo interceptar eventos si el item está seleccionado y el movimiento está activado
            if (
              isSelected &&
              currentObjectID === id &&
              !isBlocked &&
              !isModalOpen &&
              dimensionsLinesMovable
            ) {
              handleMeshPointerMove(e)
            }
          }}
          onPointerUp={(e) => {
            if (isPointerDownRef.current || isDraggingFromMesh || isDraggingRef.current) {
              handleMeshPointerUp(e)
            }
          }}
        >
          {/* Outline de selección que reemplaza visualmente el gizmo - debe estar dentro del grupo con los meshes */}
          <SelectionOutline
            id={id}
            width={item.width}
            height={item.height}
            thickness={item.thickness}
            parentGroupRef={sizeRef}
            forceVisible={isSelected && !isBlocked}
            meshRevision={glb?.uuid ?? 'pending'}
          />
          {pivotInteractable && dimensionsLinesMovable && (
            <mesh
              name={ITEM_DRAG_COLLIDER_NAME}
              position={[item.width / 2, item.height / 2, item.thickness / 2]}
              visible={false}
            >
              <boxGeometry
                args={[
                  Math.max(item.width, 1),
                  Math.max(item.height, 1),
                  Math.max(item.thickness, 1),
                ]}
              />
              <meshBasicMaterial
                transparent
                opacity={0}
                depthWrite={false}
              />
            </mesh>
          )}
          <SceneObjectRenderer
            key={`${glb?.uuid}-${contadorModificaciones}`}
            object={glb}
            zones={zones}
            morphs={morphs}
            textures={textures}
            renderTarget={renderTarget}
            paginaPdf={item.paginaPdf}
            itemHeight={item.height}
            itemWidth={item.width}
            universal={item.universal}
            id={id}
          />
        </group>
      </group>
    </PivotControls>
  )
})
